import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';

const UNAUTHENTICATED_PAGE_ROUTE_NAMES = new Set([
    'OAuthAuthorize',
    'Setup',
    'Inactive',
    'NovusOnly',
    'InvitationError',
    'Confirmation',
    'PostRegisterSteps',
    'CompleteUser',
    'Register',
    'ExpiredLink',
    'ResetPassword',
    'ForgotPasswordForm',
    'ValidateFeedbackFormExternal',
]);

export function isOnUnauthenticatedPage (routeName) {
    return UNAUTHENTICATED_PAGE_ROUTE_NAMES.has(routeName);
}

export function shouldShowNavItems (route, isAuthenticated) {
    if (!isAuthenticated) return false;

    const routeName = route?.name;

    return (
        !isOnUnauthenticatedPage(routeName) &&
        !isOnReplayPlayerPage(route?.path) &&
        !isOnConversationalAiPage(route?.path) &&
        !isOnAtkWizardPage(route?.path)
    );
}

export function isOnReplayPlayerPage (path) {
    return Boolean(path) && (path.includes('/replay/player') || path.includes('/replay/clip/saved') || path.includes('/replay/live'));
}

export function isOnListenPage (route) {
    return !!route?.matched?.filter((matchedRoute) => matchedRoute.path === '/s/:sub/listen')?.length;
}

export function isOnDataSyncPage (path) {
    return Boolean(path) && path.includes('/data-management');
}

export function isOnConversationalAiPage (path) {
    return Boolean(path) && path.includes('/guides/guide-editor');
}

export function isOnAtkWizardPage (path) {
    return Boolean(path) && path.includes('/agent-toolkit/wizard');
}

export function mapUpgradeKeyToRoute (subIdRoutesObj, featureKey) {
    const flatRouteList = flattenRoutes(subIdRoutesObj);

    const route = flatRouteList.find((route) => route.name?.toLowerCase() === featureKey.toLowerCase());

    return route;
}

export const persistedQueries = ['version', 'locale', 'revision'];

export function getQueryParamsFromAngularRoute (route) {
    if (!route) return {};

    const params = Object.entries(route.params || {});
    const pathParams = Object.keys(route.pathParams || {});

    return Object.fromEntries(params.filter(([key]) => !pathParams.includes(key)));
}

// this converts any query params that have array values
// into multiple key-value pairs with the same key
export function formatParams (paramsToFormat) {
    const params = new URLSearchParams();

    map(paramsToFormat, (value, key) => {
        if (Array.isArray(paramsToFormat[key])) {
            map(value, (item) => params.append(key, item));
        } else {
            params.append(key, value);
        }
    });

    return params;
}

export function getQueryParams (relativeUrlStr) {
    const url = new URL(relativeUrlStr, window.location.origin);
    const params = new URLSearchParams(url.search);

    return Object.fromEntries(params);
}

// builds query string manually as URLSearchParams.toString()
// encodes differently to the Vue router, which causes issues
export function buildQueryStringFromSearchParams (params) {
    let queryString = '?';

    for (const [key, value] of params.entries()) {
        queryString += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
    }

    return queryString.slice(0, -1);
}

export function flattenRoutes (routes) {
    if (isEmpty(routes)) {
        return routes;
    }

    return routes.reduce((acc, route) => {
        const { children } = route;

        const flatRoutes = [...acc, ...(children ? flattenRoutes(children) : []), route];

        return flatRoutes;
    }, []);
}
